import { MapPin } from "lucide-react";
import { TID } from "@/lib/testIds";

const PROJECT_IMG_1 =
  "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=1400&q=80&auto=format&fit=crop";
const PROJECT_IMG_2 =
  "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1400&q=80&auto=format&fit=crop";

const presence = [
  { key: "india", label: "India · Head Office", status: "Operational" },
  { key: "united-kingdom", label: "United Kingdom · Branch", status: "Operational" },
  { key: "middle-east", label: "Middle East", status: "Expansion Planned" },
];

const industries = [
  "Automotive",
  "Retail & E-Commerce",
  "Pharmaceuticals",
  "Healthcare",
  "FMCG",
  "Industrial Manufacturing",
  "Engineering",
  "Chemicals",
  "Energy",
  "Construction",
  "Technology",
  "Textiles",
];

export default function About() {
  return (
    <section
      id="about"
      data-testid={TID.about.section}
      className="bg-slate-50 border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 space-y-10">
            <div className="reveal">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-600 mb-4">
                About JMR Global Logistics
              </p>
              <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.02] mb-6">
                Built for<br />
                <span className="text-slate-400">a borderless world.</span>
              </h2>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed max-w-3xl mb-5">
                JMR Global Logistics was established with a clear vision — to provide world-class logistics services backed by integrity, transparency, and operational excellence. With operations in India and the United Kingdom, and strategic expansion planned across the Middle East, we are building a strong global logistics network capable of supporting businesses of every size.
              </p>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed max-w-3xl">
                Our commitment extends beyond transporting cargo. We build long-term partnerships by delivering dependable logistics solutions that create value for our customers and overseas partners. Every shipment is managed with precision, professionalism, and personal attention.
              </p>
            </div>

            <div className="relative reveal">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <img
                  src={PROJECT_IMG_1}
                  alt="JMR Global Logistics project cargo heavy lift at port — containers and breakbulk"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[240px] md:h-[340px] object-cover border border-slate-200"
                />
                <img
                  src={PROJECT_IMG_2}
                  alt="Heavy industrial project cargo handling — cranes and container ship"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[240px] md:h-[340px] object-cover border border-slate-200"
                />
              </div>
              <div className="absolute bottom-0 left-0 bg-blue-900 text-white px-6 py-4 max-w-xs">
                <p className="font-mono text-xs text-sky-400 mb-1">// PROJECT CARGO</p>
                <p className="font-display font-bold text-lg leading-tight">Heavy-lift &amp; oversized cargo handled with precision.</p>
              </div>
            </div>

            <div className="reveal">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 mb-6">
                OUR GLOBAL FOOTPRINT
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-slate-300">
                {presence.map((t, i) => (
                  <div
                    key={t.key}
                    className={`p-6 border-b border-slate-300 ${i < 2 ? "sm:border-r" : ""}`}
                  >
                    <div className="font-display font-black text-2xl md:text-3xl text-slate-900 mb-2 leading-tight">{t.label}</div>
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 ${t.status === "Operational" ? "bg-green-500" : "bg-sky-500"} animate-pulse`} />
                      <span className="text-xs uppercase font-mono tracking-wider text-slate-500">{t.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside
            data-testid={TID.industries.section}
            className="lg:col-span-4 reveal"
            id="industries"
          >
            <div className="bg-blue-900 text-white p-8 sticky top-24">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-400 mb-4">
                Industries We Support
              </p>
              <h3 className="font-display font-bold text-3xl mb-8 leading-tight">
                Cargo for every<br />vertical.
              </h3>
              <ul className="space-y-3">
                {industries.map((ind, i) => (
                  <li
                    key={ind}
                    data-testid={TID.industries.item(ind.toLowerCase().replace(/[^a-z0-9]+/g, "-"))}
                    className="flex items-center gap-3 group cursor-default"
                  >
                    <span className="font-mono text-sm text-slate-400 w-6">{String(i + 1).padStart(2, "0")}</span>
                    <MapPin className="w-4 h-4 text-sky-400 shrink-0" />
                    <span className="font-medium text-base text-slate-100 group-hover:text-sky-300 transition-colors">{ind}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
